/**
 * @providesModule UserPropertiesController
 */

import NetClient from 'NetClient';
import {NativeModules} from 'react-native';
import {AllUserPropertiesKeys} from 'UserPropertiesConstants';

const SKZAPIManager = NativeModules.SKZAPIManager;

export type UserPropertyCategories = 'ftue' | 'withdrawal'; // Add more categories as needed

/** The full scope of all key-value pairs that can be accessed from the server db. Note that any and all keys are optional. Server will only respond with keys if they have already been created in the db. */
type UserPropertiesData = {
  key: AllUserPropertiesKeys;
  value: string;
};

/**
 * Get all user properties for a given category. Only keys that exist in the db for that given category will be returned. Categories are determined by the string before the first "." in the key, e.g. "ftue.store" is in the "ftue" category.
 * @param category The category of user properties to retrieve.
 * @returns A promise that resolves with the user properties for the given category.
 */
export const getUserPropertiesByCategory = async (category: UserPropertyCategories) => {
  try {
    const response: UserPropertiesData[] | null = await NetClient.getURL(
      '/user/v1/properties',
      {category},
      SKZAPIManager.OnError.failSilently
    );

    if (!response) {
      return null;
    }

    return response;
  } catch (error) {
    console.warn('Error fetching all user properties: ', error);
    return null;
  }
};

/**
 * Update a given user property. Will create the key-value pair if it does not exist, and update the value if it does exist.
 * @param key The key of the user property to update.
 * @param value The value of the user property to update.
 * @returns A promise that resolves with the same data you passed in.
 */
export const updateUserProperties = async (key: AllUserPropertiesKeys, value: string) => {
  const data = {
    key,
    value,
  };
  return new Promise(
    (resolve: (data: UserPropertiesData) => void, reject: (error: Error | null) => void) => {
      const url = '/user/v1/properties';
      NetClient.patchURL(
        url,
        data,
        SKZAPIManager.OnError.failSilently,
        (error: Error | null, payload: UserPropertiesData) => {
          if (payload && !error) {
            resolve(payload);
          } else {
            reject(error);
          }
        }
      );
    }
  );
};
